import { type Patient } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/patient';
import { type Section } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/section';
import {
  type PrescriptionStatus,
  PrescriptionStatusMap,
} from '@alto/scriptdash/alto/patient_app/types/v1/prescription_status';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useQuery } from '@tanstack/react-query';
import { get } from '~shared/helpers/apiHelper';
import { staleTimes } from '~shared/queries/constants';

export type HomescreenQueryData = {
  data: {
    sections: Section[];
    patient: Patient;
  };
};

const statuses: PrescriptionStatus[] = [
  PrescriptionStatusMap.RECEIVED_BY_ALTO,
  PrescriptionStatusMap.PHARMACIST_REVIEW,
  PrescriptionStatusMap.SEARCHING_FOR_SAVINGS,
  PrescriptionStatusMap.PRIOR_AUTH_IN_PROGRESS,
  PrescriptionStatusMap.READY,
];

export const homescreenMethods = createQueryKeys('homescreenMethods', {
  fetchHomescreen: ({ userID }: { userID: number | null | undefined }) => ({
    queryKey: [{ userID }],
    queryFn: fetchHomescreen,
  }),
});

export function useFetchHomescreen(userID: number | undefined | null) {
  const { data, isFetching, isLoading, refetch, isError } = useQuery({
    ...homescreenMethods.fetchHomescreen({ userID }),
    staleTime: staleTimes.oneMinute,
    enabled: !!userID,
  });

  return {
    data,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
}

/**
 * We would prefer using auto-generated endpoints directly with `apiEndpointHandler`
 * @see https://github.com/scriptdash/clients/pull/7913
 */
const fetchHomescreen = async () => {
  return get('/homescreen', { statuses }, { version: 'v1', splitArrayParams: true }).then((response) => {
    if (response.error) {
      throw new Error(response.error);
    }
    return response as HomescreenQueryData;
  });
};
